import { Search as SearchIcon } from '@mui/icons-material';
import { FilledInputProps, TextField } from '@mui/material';
import { ClearButton } from 'components/buttons';
import { debounce, omit } from 'lodash';
import React, { forwardRef, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import useSearchStyles from './styles';
import { SearchProps } from './types';

const Search = forwardRef<HTMLInputElement, SearchProps>((props, ref) => {
  const {
    clearable = true,
    value: propValue = '',
    onChange: propOnChange = () => ({}),
    debounceTimeout = 500,
  } = props;

  const textProps = omit(props, ['clearable', 'debounceTimeout', 'value', 'onChange']);
  const { t } = useTranslation('common');
  const classes = useSearchStyles(props);

  const [value, setValue] = useState<string>(propValue);
  useEffect(() => {
    if (propValue !== value) {
      setValue(propValue);
    }
    // only do this when the propValue has changed
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [propValue]);

  const inputRef = useRef<HTMLInputElement>(null);

  const debouncedOnChange = useMemo(
    () => debounce(propOnChange, debounceTimeout),
    // exclude onChange
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [debounceTimeout]
  );

  useEffect(() => {
    // check if mounted, cause we don't want to fire propOnChange on the initial mounting render
    if (!mountedRef.current || !inputRef.current || !propOnChange) {
      return;
    }
    // this feels gross, but its the only way I can figure out
    // to successfully pass an object that should work for React.ChangeEvent
    // and avoid changing the interface for onChange
    const event = new Event('input', { bubbles: true });
    const targetedEvent = {
      ...event,
      target: inputRef.current,
      currentTarget: inputRef.current,
    };
    const fakeEvent: React.ChangeEvent<HTMLInputElement> = {
      ...targetedEvent,
      nativeEvent: event,
      isDefaultPrevented: () => false,
      isPropagationStopped: () => false,
      persist: () => null,
    };
    debouncedOnChange(fakeEvent);
    // only fire when the value changes. assume the callbacks are not stale
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value]);

  const onChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    setValue(e.target.value);
  }, []);

  const onClear = useCallback(() => {
    setValue('');
  }, []);

  const clearButton = useMemo(() => <ClearButton onClick={onClear} />, [onClear]);

  const mountedRef = useRef(false);
  useEffect(() => {
    mountedRef.current = true;
  }, []);

  const endAdornment = useMemo(
    () => (
      <>
        {clearable && !!value?.length && clearButton}
        {props.InputProps?.endAdornment}
      </>
    ),
    [clearButton, clearable, props.InputProps?.endAdornment, value?.length]
  );

  const InputProps: Partial<FilledInputProps> = {
    startAdornment: <SearchIcon className={classes.searchIcon} />,
    classes: { input: classes.input, underline: classes.underline },
    ...props.InputProps,
    endAdornment,
  };

  return (
    <TextField
      margin='none'
      ref={ref}
      placeholder={t('search')}
      InputProps={InputProps}
      variant='filled'
      inputRef={inputRef}
      {...textProps}
      value={value}
      onChange={onChange}
    />
  );
});

export default Search;
